import { Button, Flex, Form, List, Modal } from 'antd';
import ActionHeader from '../../../../components/actionHeader';
import { StyledListItem } from '../../../../styles/listItem';
import { useEffect, useState, useMemo } from 'react';
import {
  fetchTemplateCauses,
  fetchTemplateIssues,
  selectIsFetchingTemplates,
  selectTemplateCauses,
  selectTemplateCurrentCauses,
  selectTemplateCurrentIssues,
  selectTemplateRejectedCauses,
  selectTemplateToBeReviewedCauses,
} from '../../../../slices/templates';
import { useParams } from 'react-router';
import { useAppDispatch } from '../../../../store';
import { useSelector } from 'react-redux';
import { ListContainer } from './styles';
import {
  fetchSolutionGrid,
  selectCurrentSolutionGridVersions,
  selectSolutionGrids,
} from '../../../../slices/solutionGrids';
import { initSolutionGrid } from '../../../../utils/dataMapper';
import { createSolutionGrid, deleteCause } from '../../../../utils/httpRequest';
import { Cause, NewOrExisting } from '../../../../types';
import CauseForm from '../../../../components/causeForm';
import CauseCard from '../../../../components/causeCard';
import Tab from '../../../../components/tab';

const TemplateCauses = () => {
  const { id = '' } = useParams();
  const dispatch = useAppDispatch();
  const [openForm, setOpenForm] = useState(false);
  const [causeId, setCauseId] = useState('');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const issues = useSelector(selectTemplateCurrentIssues);
  const causes = useSelector(selectTemplateCauses);
  const isFetching = useSelector(selectIsFetchingTemplates);
  const solutionGrids = useSelector(selectSolutionGrids);
  const currentSolutionGridVersions = useSelector(
    selectCurrentSolutionGridVersions
  );
  const currentCauses = useSelector(selectTemplateCurrentCauses);
  const toBeReviewedCauses = useSelector(selectTemplateToBeReviewedCauses);
  const rejectedCauses = useSelector(selectTemplateRejectedCauses);

  const linkedCauseIds = useMemo(() => {
    return new Set(solutionGrids.map((grid) => String(grid.causeID)));
  }, [solutionGrids]);

  const causeItem = causes?.find((i) => i.causeID === causeId);

  const handleSearch = (searchText?: string) => {
    if (!isFetching) {
      dispatch(
        fetchTemplateCauses({
          templateId: id,
          params: { search: searchText },
        })
      );
    }
  };
  const handleAddClick = () => {
    form.resetFields();
    setCauseId('');
    setOpenForm(true);
  };

  const handleEdit = (causeID: string) => {
    const item = causes?.find((i) => i.causeID === causeID);
    form.setFieldsValue(item);
    setOpenForm(true);
    setCauseId(causeID);
  };

  const handleDelete = async (issueID: string) => {
    try {
      setLoading(true);
      await deleteCause(issueID);
      dispatch(fetchTemplateCauses({ templateId: id }));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (cause: Cause) => {
    setOpenForm(false);
    if (!solutionGrids.length) {
      const { newSolutionGrid } = initSolutionGrid({
        issues: issues || [],
        causes: [cause],
        templateID: id,
        existingSolutionGrids: solutionGrids || [],
        versionID: currentSolutionGridVersions?.versionID,
      });

      if (newSolutionGrid?.length) {
        createSolutionGrid(
          {
            newOrExisting: solutionGrids.length
              ? NewOrExisting.EXISTING
              : NewOrExisting.NEW,
            versionID: currentSolutionGridVersions?.versionID,
            solutionGridDtoList: newSolutionGrid,
          },
          id
        );
      }
    }
    dispatch(fetchTemplateCauses({ templateId: id }));
  };

  useEffect(() => {
    if (!issues?.length) {
      dispatch(fetchTemplateIssues({ templateId: id }));
    }
    if (!causes?.length) {
      dispatch(fetchTemplateCauses({ templateId: id }));
    }
    if (!solutionGrids?.length) {
      dispatch(fetchSolutionGrid({ templateID: id }));
    }
  }, []);

  const tabs = [
    {
      id: 1,
      title: `Current (${currentCauses.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={currentCauses}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <CauseCard
                  cause={item}
                  showEdit
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                  showLinkedBadge
                  isLinked={linkedCauseIds.has(String(item.causeID))}
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
    {
      id: 2,
      title: `To-Be-Reviewed (${toBeReviewedCauses.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={toBeReviewedCauses}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <CauseCard
                  cause={item}
                  showEdit
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                  showLinkedBadge
                  isLinked={linkedCauseIds.has(item.causeID)}
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
    {
      id: 3,
      title: `Rejected (${rejectedCauses.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={rejectedCauses}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <CauseCard
                  cause={item}
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
  ];

  return (
    <Flex vertical flex={1} gap={8} style={{ height: '100%' }}>
      <ActionHeader
        title="Cause"
        isSecondaryTitle
        showBorder={false}
        showSearch
        onSearch={handleSearch}
        endSlot={
          <>
            <Button type="primary" disabled ghost onClick={handleAddClick}>
              Batch Upload
            </Button>
            <Button type="primary" onClick={handleAddClick}>
              Add New Cause
            </Button>
          </>
        }
      />
      <Flex flex={1} style={{ overflowY: 'auto' }}>
        <Tab tabs={tabs} />
      </Flex>
      <Modal
        title={causeId ? `Edit ${causeItem?.name}` : 'Add New Cause'}
        open={openForm}
        footer={null}
        width={800}
        destroyOnClose
        onCancel={() => setOpenForm(false)}
        onClose={() => setOpenForm(false)}
      >
        <Form form={form} layout="vertical">
          <CauseForm
            templateID={id}
            form={form}
            onCancel={() => setOpenForm(false)}
            onSubmit={handleSubmit}
            cause={causeItem}
          />
        </Form>
      </Modal>
    </Flex>
  );
};

export default TemplateCauses;
